import { useBreakpointIndex } from '@theme-ui/match-media';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useEffect, useState } from 'react';
import ReactSlickSlider, { Settings } from 'react-slick';

import {
  Box,
  BreadCrumbs,
  Button,
  CertifiedBadge,
  Container,
  Flex,
  Grid,
  Heading,
  Link,
  Modal,
  Paragraph,
  Section,
  SliderNextArrow,
  SliderPrevArrow,
  Spacer,
  Text,
  useElementOutOfView,
} from 'voom-gatsby';

import {
  BunionplastyBackground,
  ConsultationTag,
  DoctorStickyBar,
  PhoneLink,
} from '~components';

import { ReactComponent as CalendarIcon } from '~svg/icons/icon-calendar.svg';
import { ReactComponent as GalleryIcon } from '~svg/icons/icon-gallery.svg';
import { ReactComponent as GeoMarkerIcon } from '~svg/icons/icon-geomarker.svg';
import { ReactComponent as PhoneIcon } from '~svg/icons/icon-phone.svg';

import { SanityDoctor, SanityDoctorOffice } from '~types/sanity';

import { DoctorModalForm } from './DoctorModalForm';
import { SearchBadgeImage } from '~components/SearchBadgeImage';
import { AppointmentLink } from '~components/AppointmentLink';

const doctorHeroCarouselSettings: Settings = {
  dots: false,
  slidesToScroll: 1,
  slidesToShow: 1,
  prevArrow: (
    <SliderPrevArrow
      sx={{
        position: ['fixed', null, null, 'absolute'],
        left: [0, null, null, -8],
      }}
    />
  ),
  nextArrow: (
    <SliderNextArrow
      sx={{
        position: ['fixed', null, null, 'absolute'],
        right: [0, null, null, -8],
      }}
    />
  ),
  responsive: undefined,
};
interface DoctorHeroProps {
  doctor: SanityDoctor;
  showFormOnLoad: boolean;
  selectedOffice: SanityDoctorOffice;
  setSelectedOffice: (office: SanityDoctorOffice) => void;
}

export const DoctorHero = ({
  doctor,
  showFormOnLoad,
  selectedOffice,
  setSelectedOffice,
}: DoctorHeroProps) => {
  const restOfOffices = doctor.doctorOffices.filter(
    (office) => office.streetAddress !== selectedOffice.streetAddress,
  );
  const mainOffice = doctor.doctorOffices[0];
  const { ref, isOutOfView } = useElementOutOfView();
  const breakpointIndex = useBreakpointIndex();
  const mobileView = breakpointIndex < 2;

  const buttonText = 'Appointment';

  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const openGalleryModal = () => setShowGalleryModal(true);
  const closeGalleryModal = () => setShowGalleryModal(false);

  const [showFormModal, setShowFormModal] = useState(false);
  const openFormModal = () => setShowFormModal(true);
  const closeFormModal = () => setShowFormModal(false);

  useEffect(() => {
    if (showFormOnLoad === true) {
      openFormModal();
    }
  }, [showFormOnLoad]);

  const [breadcrumbUrl, setBreadcrumbUrl] = useState<string>(
    '/find-a-doctor/doctors',
  );
  const isBrowser = typeof window !== 'undefined';
  useEffect(() => {
    if (isBrowser) {
      const url = window.localStorage.getItem('doctorSearchResultUrl');
      if (url) {
        setBreadcrumbUrl(url);
      }
    }
  }, [isBrowser]);
  return (
    <>
      <Section
        sx={{
          py: [5, null, 8, null, 9],
          bg: 'beige',
          overflow: 'hidden',
        }}
        ref={ref}
      >
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <BunionplastyBackground
            sx={{
              left: [-250, null, -425],
              top: [-350, null, -400],
              width: [593, null, 1050],
              height: [560, null, 987],
              zIndex: -1,
            }}
          />
          <Flex sx={{ flexDirection: ['column', null, 'row'] }}>
            <Box
              sx={{
                flex: ['0 0 100%', null, '0 0 50%'],
                position: 'relative',
              }}
            >
              <GatsbyImage
                image={doctor.gallery[0].asset?.gatsbyImageData}
                alt={doctor.gallery[0].alt}
                sx={{
                  borderRadius: 'corner',
                  height: 'full',
                  maxHeight: 700,
                  // aspectRatio: ['315 / 225', null, '800 / 595'],
                  img: {
                    borderRadius: 'corner',
                    // aspectRatio: ['315 / 225', null, '800 / 595'],
                    objectPosition: 'top',
                  },
                }}
                loading="eager"
              />
              <CertifiedBadge sx={{ top: 'unset', bottom: 0 }} />
              {doctor.gallery.length > 1 && (
                <>
                  <Button
                    variant="iconGhost"
                    aria-label="View Gallery"
                    onClick={openGalleryModal}
                    sx={{
                      position: 'absolute',
                      top: [3, 4],
                      left: [3, 4],
                      width: 50,
                      height: 50,
                      p: 0,
                      svg: {
                        width: 50,
                        height: 50,
                      },
                    }}
                  >
                    <GalleryIcon sx={{ color: 'primary' }} />
                  </Button>
                  <Modal
                    close={closeGalleryModal}
                    open={openGalleryModal}
                    isOpen={showGalleryModal}
                    sx={{
                      '&[data-reach-dialog-content]': {
                        bg: 'transparent',
                        overflow: 'visible',
                      },
                    }}
                  >
                    <Grid
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        placeContent: 'center',
                      }}
                    >
                      <ReactSlickSlider
                        {...doctorHeroCarouselSettings}
                        sx={{ width: '90vw', maxHeight: '80vh' }}
                      >
                        {doctor.gallery.map((image, index) => {
                          return (
                            <Box
                              key={image.alt + index}
                              sx={{
                                height: '80vh',
                                width: '90vw',
                              }}
                            >
                              <GatsbyImage
                                key={image.alt + index}
                                image={image.asset.gatsbyImageData}
                                alt={image.alt}
                                imgStyle={{ objectFit: 'contain' }}
                                sx={{
                                  height: 'full',
                                  width: 'full',
                                  borderRadius: 'corner',
                                  img: {
                                    borderRadius: 'corner',
                                  },
                                }}
                              />
                            </Box>
                          );
                        })}
                      </ReactSlickSlider>
                    </Grid>
                  </Modal>
                </>
              )}
            </Box>
            <Flex
              sx={{
                flex: ['0 0 100%', null, '0 0 44%'],
                px: [0, null, 6],
                py: [3, null, 6],
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <BreadCrumbs
                links={[
                  { url: breadcrumbUrl, label: 'All Doctors' },
                  {
                    url: `/find-a-doctor/doctors/${doctor.slug.current}`,
                    label: doctor.name,
                  },
                ]}
                sx={{ mb: 0 }}
              />
              <Heading variant="contentTitle" as="h1">
                <Text sx={{ fontWeight: 'medium', mr: 2 }}>{doctor.name}</Text>
                <Text>{doctor.credentials}</Text>
                <SearchBadgeImage doctor={doctor} mobileView={mobileView}></SearchBadgeImage>
              </Heading>
              <Spacer space={1} />
              <Paragraph variant="mediumP" sx={{ mb: 0 }}>
                <GeoMarkerIcon
                  sx={{
                    mr: 2,
                    width: [15, null, null, 16],
                    height: [15, null, null, 16],
                  }}
                />
                <Text sx={{ fontSize: ['xs', null, null, 'md'] }}>
                  {selectedOffice.streetAddress} {selectedOffice.city.name},{' '}
                  {selectedOffice.state.abbreviation} {selectedOffice.zip}
                </Text>
              </Paragraph>
              <Spacer space={1} />
              <PhoneLink office={mainOffice} />
              <Spacer space={2} />
              <Paragraph variant="mediumP" sx={{ mb: 0, ml: 4 }}>
                <Text
                  sx={{
                    fontSize: 'xs',
                    fontWeight: 'medium',
                    pr: 2,
                  }}
                >
                  {restOfOffices.length > 0 && 'Locations in:'}
                </Text>
                {restOfOffices.map((office, index) => (
                  <Text key={office.streetAddress}>
                    <Text
                      key={office.city.name + index}
                      sx={{
                        textDecoration: 'underline',
                        pr: 2,
                        color: 'grey500',
                        fontSize: 'xs',
                        fontWeight: 'light',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      onClick={() => setSelectedOffice(office)}
                    >
                      {office.city.name}, {office.state.abbreviation}
                    </Text>
                    <Text
                      sx={{
                        pr: 2,
                        color: 'grey500',
                        fontSize: 'xs',
                        fontWeight: 'light',
                      }}
                    >
                      {index < restOfOffices.length - 1 && ' | '}
                    </Text>
                  </Text>
                ))}
              </Paragraph>
              <Spacer space={2} />
              <Flex sx={{ mt: 'auto' }}>
                <AppointmentLink telehealthEmbedCode={doctor.telehealthEmbedCode} variant="primary"
                  onClick={openFormModal}
                >
                </AppointmentLink>
                <Spacer direction={'horizontal'} space={2} />
                <PhoneLink office={mainOffice} ignoreMobile={true} variant="button" />
              </Flex>
              <DoctorModalForm
                close={closeFormModal}
                open={openFormModal}
                isOpen={showFormModal}
                doctor={doctor}
                locations={doctor.doctorOffices}
                selectedOffice={selectedOffice || doctor.doctorOffices[0]}
                setSelectedOffice={setSelectedOffice}
              />
            </Flex>
          </Flex>
        </Container>
      </Section>
      <DoctorStickyBar
        doctor={doctor}
        active={isOutOfView}
        openFormModal={openFormModal}
        selectedOffice={selectedOffice || doctor.doctorOffices[0]}
      />
    </>
  );
};
